import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';

const { FiTrendingUp, FiUsers, FiClock, FiShield, FiGift, FiZap, FiCheckCircle } = FiIcons;

const PricingStrategies = () => {
  const { t } = useLanguage();

  const STRATEGIES = [
    /*
    {
      id: 'dynamic',
      title: t("strategiesSection.items.dynamic.title"),
      icon: FiTrendingUp,
      desc: t("strategiesSection.items.dynamic.desc"),
      benefit: t("strategiesSection.items.dynamic.benefit"),
      tag: t("strategiesSection.items.dynamic.tag")
    },
    */
    {
      id: 'early',
      title: t("strategiesSection.items.early.title"),
      icon: FiClock,
      desc: t("strategiesSection.items.early.desc"),
      benefit: t("strategiesSection.items.early.benefit"),
      tag: t("strategiesSection.items.early.tag")
    },
    {
      id: 'group',
      title: t("strategiesSection.items.group.title"),
      icon: FiUsers,
      desc: t("strategiesSection.items.group.desc"),
      benefit: t("strategiesSection.items.group.benefit"),
      tag: t("strategiesSection.items.group.tag")
    },
    {
      id: 'member',
      title: t("strategiesSection.items.member.title"),
      icon: FiShield,
      desc: t("strategiesSection.items.member.desc"),
      benefit: t("strategiesSection.items.member.benefit"),
      tag: t("strategiesSection.items.member.tag")
    },
    {
      id: 'referral',
      title: t("strategiesSection.items.referral.title"),
      icon: FiGift,
      desc: t("strategiesSection.items.referral.desc"),
      benefit: t("strategiesSection.items.referral.benefit"),
      tag: t("strategiesSection.items.referral.tag")
    },
    {
      id: 'flash',
      title: t("strategiesSection.items.flash.title"),
      icon: FiZap,
      desc: t("strategiesSection.items.flash.desc"),
      benefit: t("strategiesSection.items.flash.benefit"),
      tag: t("strategiesSection.items.flash.tag")
    }
  ];

  const [activeStrategyId, setActiveStrategyId] = useState(STRATEGIES[0].id);
  const activeStrategy = STRATEGIES.find(s => s.id === activeStrategyId) || STRATEGIES[0];

  return (
    <section id="strategies" className="py-24 bg-white overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold uppercase tracking-wider mb-4">
            <SafeIcon icon={FiTrendingUp} className="w-4 h-4" /> {t('strategiesSection.badge')}
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
            {t('strategiesSection.title')} <span className="text-gradient">{t('strategiesSection.titleSpan')}</span>
          </h2>
          <p className="text-lg text-slate-600">
            {t('strategiesSection.desc')}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left Side: Strategy List */}
          <div className="lg:col-span-5 space-y-4">
            {STRATEGIES.map((strategy) => (
              <button
                key={strategy.id}
                onClick={() => setActiveStrategyId(strategy.id)}
                className={`w-full text-left p-6 rounded-2xl border transition-all duration-300 group ${
                  activeStrategy.id === strategy.id
                    ? 'bg-indigo-600 border-indigo-600 shadow-xl shadow-indigo-200'
                    : 'bg-white border-slate-100 hover:border-indigo-200 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                    activeStrategy.id === strategy.id ? 'bg-white/20 text-white' : 'bg-indigo-50 text-indigo-600'
                  }`}>
                    <SafeIcon icon={strategy.icon} className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className={`font-bold transition-colors ${
                      activeStrategy.id === strategy.id ? 'text-white' : 'text-slate-900'
                    }`}>
                      {strategy.title}
                    </h3>
                    <p className={`text-xs mt-0.5 transition-colors ${
                      activeStrategy.id === strategy.id ? 'text-indigo-100' : 'text-slate-500'
                    }`}>
                      {strategy.tag}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Right Side: Interactive Preview */}
          <div className="lg:col-span-7 relative">
            <div className="absolute -inset-10 bg-gradient-to-tr from-indigo-500/10 to-violet-500/10 rounded-full blur-3xl -z-10"></div>
            
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStrategy.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                className="bg-slate-900 rounded-[32px] p-8 md:p-12 shadow-2xl border border-slate-800"
              >
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
                  <div>
                    <span className="text-indigo-400 text-sm font-bold uppercase tracking-widest block mb-2">{t('strategiesSection.activeStrategy')}</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-white">{activeStrategy.title}</h3>
                  </div>
                  <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                    <span className="text-emerald-400 text-sm font-bold">{activeStrategy.benefit}</span>
                  </div>
                </div>

                <p className="text-slate-400 text-lg leading-relaxed mb-10">
                  {activeStrategy.desc}
                </p>

                <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
                  <h4 className="text-white font-bold mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiCheckCircle} className="text-indigo-400" /> {t('strategiesSection.keyCapability')}
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50">
                      <div className="text-slate-500 text-xs font-bold uppercase mb-1">{t('strategiesSection.cap1Title')}</div>
                      <div className="text-slate-200 text-sm">{t('strategiesSection.cap1Desc')}</div>
                    </div>
                    <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50">
                      <div className="text-slate-500 text-xs font-bold uppercase mb-1">{t('strategiesSection.cap2Title')}</div>
                      <div className="text-slate-200 text-sm">{t('strategiesSection.cap2Desc')}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-10 flex flex-wrap gap-4">
                  <button className="bg-indigo-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-indigo-500 transition-all shadow-lg shadow-indigo-600/20">
                    {t('strategiesSection.ctaConfig')}
                  </button>
                  <button className="text-slate-400 hover:text-white px-8 py-3 rounded-xl font-bold transition-colors">
                    {t('strategiesSection.ctaDoc')}
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingStrategies;